import { useState, useEffect } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import pMap from 'p-map-browser';
import axios from 'axios';

import {
  cardCreate as cardCreateMutation,
  cardUpdate as cardUpdateMutation,
} from '../../../apps/cards/graphql/mutations/card';
import {
  CARD_BORDER_TYPE,
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
  PHOTO_TYPE,
} from '../../../services/cards/constants';
import getPrintProducts from '../../../services/cards/graphql/queries/getPrintProducts';
import getCardQuery from '../../../services/cards/graphql/queries/getCard';
import getPrintSizeCard from '../../../services/prints/graphql/queries/getPrintSizeCard';
import { isFullFrame } from '../../../services/cards/components/AccountCard/helpers';
import { getCardSide, MAX_Z_INDEX } from './helpers';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import { cardElementCreate as cardElementCreateMutation } from '../../../apps/cards/graphql/mutations/cardElement';
import { cardTemplateUpdate as cardTemplateUpdateMutation } from '../../../apps/cards/graphql/mutations/cardTemplate';
import { cardElementConfigurationCreate as cardElementConfigurationCreateMutation } from '../../../apps/cards/graphql/mutations/cardElementConfiguration';
import getPrintPartners from '../../../services/cards/graphql/queries/getPrintPartners';
import { getImageBasedResizedFrame } from './components/CardDesignerCardElements/CardDesignerCardElementControls/components/CardDesignerCardElementResizeControls/helpers';
import { BASE_LOGO_FRAME } from './components/CardDesignerControlPanel/components/CardDesignerLogoControls/helpers';
import { assetImagesUpload as assetImagesUploadMutation } from '../../../graphql/mutations/account';
import { MeQuery } from '../../../graphql/queries/account';
import { getAsset } from '../../../util/assets';
import { DESIGN_MODES } from './constants';
import { isStandardPrint } from '../../../util/print-product';

const getDefaultPrintProduct = printProducts => {
  if (!printProducts) {
    return undefined;
  }

  return printProducts.find(printProduct => isStandardPrint(printProduct));
};

const eventCollagePresetPhotoSlots = [
  // Central Element
  {
    input: {
      type: CARD_ELEMENT_TYPE.PHOTO,
      z_index: 6,
      frame: {
        width: 0.44,
        height: 0.38,
        x: 0.28,
        y: 0.31,
      },
    },
  },
  // Top Left
  {
    input: {
      type: CARD_ELEMENT_TYPE.PHOTO,
      z_index: 5,
      frame: {
        width: 0.31,
        height: 0.29,
        x: 0.06,
        y: 0.18,
        rotation: -0.07,
      },
    },
  },
  // Top Right
  {
    input: {
      type: CARD_ELEMENT_TYPE.PHOTO,
      z_index: 4,
      frame: {
        width: 0.31,
        height: 0.29,
        x: 0.63,
        y: 0.18,
        rotation: -0.07,
      },
    },
  },
  // Bottom left
  {
    input: {
      type: CARD_ELEMENT_TYPE.PHOTO,
      z_index: 3,
      frame: {
        width: 0.31,
        height: 0.29,
        x: 0.07,
        y: 0.61,
        rotation: -0.22,
      },
    },
  },
  // Bottom Right
  {
    input: {
      type: CARD_ELEMENT_TYPE.PHOTO,
      z_index: 2,
      frame: {
        width: 0.31,
        height: 0.29,
        x: 0.61,
        y: 0.61,
        rotation: 0.22,
      },
    },
  },
];

const createEventCollageElements = async (
  cardSide,
  cardTemplate,
  backgroundCardElement,
  cardElementCreate,
  cardElementConfigurationCreate,
  cardTemplateUpdate,
  assetImagesUpload,
  printSize,
  me,
) => {
  try {
    let logoCardElement;
    // Create Powered By Waldo element
    const waldoCardElementCreateResponse = await cardElementCreate({
      variables: {
        input: {
          type: CARD_ELEMENT_TYPE.PHOTO,
          z_index: 1,
          frame: {
            width: 1,
            height: 1,
            x: 0,
            y: 0,
          },
        },
      },
    });
    const waldoCardElement =
      waldoCardElementCreateResponse.data.cardElementCreate.card_element;

    const fileResponse = await axios.get(
      getAsset('/powered-by-waldo-backdrop.png'),
      {
        responseType: 'blob',
      },
    );

    const file = new File(
      [fileResponse.data],
      'powered-by-waldo-backdrop.png',
      {
        type: 'image/png',
      },
    );

    const imageUploadResponse = await assetImagesUpload({
      variables: {
        input: {
          images: [
            {
              filename: file.name,
              content_type: file.type,
            },
          ],
        },
      },
    });

    const [imageUploadData] =
      imageUploadResponse.data.assetImagesUpload.image_uploads;

    await axios.put(imageUploadData.url, file, {
      headers: {
        'Content-Type': 'image/png',
      },
    });

    await cardTemplateUpdate({
      variables: {
        input: {
          card_template_id: cardTemplate.id,
          card_element_ids: [
            backgroundCardElement?.id,
            waldoCardElement?.id,
          ].filter(Boolean),
        },
      },
    });

    await cardElementConfigurationCreate({
      variables: {
        input: {
          card_element_id: waldoCardElement.id,
          card_side_id: cardSide.id,
          is_editable: false,
          photo_element_configuration: {
            asset_image_id: imageUploadData.id,
          },
        },
      },
    });

    if (me?.logo_image?.url) {
      // Cards are created in the LANDSCAPE orientation by default, but it is possible
      // the print size's values do not match said orientation. Therefore, pre-parse
      // the printSize in order to ensure it matches our expectations.
      const parsedPrintSize =
        printSize.width < printSize.height
          ? {
              ...printSize,
              width: printSize.height,
              height: printSize.width,
            }
          : printSize;

      const logoFrame = getImageBasedResizedFrame(
        parsedPrintSize,
        BASE_LOGO_FRAME,
        me.logo_image,
        true,
      );

      const logoCardElementCreateResponse = await cardElementCreate({
        variables: {
          input: {
            type: CARD_ELEMENT_TYPE.PHOTO,
            z_index: MAX_Z_INDEX,
            frame: logoFrame,
          },
        },
      });

      logoCardElement =
        logoCardElementCreateResponse.data.cardElementCreate.card_element;

      await cardTemplateUpdate({
        variables: {
          input: {
            card_template_id: cardTemplate.id,
            card_element_ids: [
              backgroundCardElement?.id,
              waldoCardElement?.id,
              logoCardElement?.id,
            ].filter(Boolean),
          },
        },
      });

      // Create the initial configuration of the logoCardElement
      await cardElementConfigurationCreate({
        variables: {
          input: {
            card_element_id: logoCardElement.id,
            card_side_id: cardSide.id,
            is_editable: false,
            photo_element_configuration: {
              existing_asset_image_id: me.logo_image.id,
            },
          },
        },
      });
    }

    // Create the photo elements.
    const photoElementIds = await pMap(
      eventCollagePresetPhotoSlots,
      async ({ input }) => {
        const { data } = await cardElementCreate({
          variables: {
            input,
          },
        });

        const photoCardElement = data.cardElementCreate.card_element;
        return photoCardElement.id;
      },
      { concurrency: 5 },
    );

    // Update the card template to include all elements.
    await cardTemplateUpdate({
      variables: {
        input: {
          card_template_id: cardTemplate.id,
          card_element_ids: [
            backgroundCardElement?.id,
            waldoCardElement?.id,
            logoCardElement?.id,
            ...photoElementIds,
          ].filter(Boolean),
        },
      },
    });

    await pMap(
      photoElementIds,
      async cardElementId => {
        await cardElementConfigurationCreate({
          variables: {
            input: {
              card_element_id: cardElementId,
              card_side_id: cardSide.id,
              is_editable: true,
              border: {
                color: '#FFFFFF',
                opacity: 1,
                type: CARD_BORDER_TYPE.SOLID,
                width: 0.05,
              },
            },
          },
        });
      },
      { concurrency: 5 },
    );
  } catch {
    //  Nothing to do here. worst case scenario card creation will follow up without these items.
  }
};

// Extends a freshly created card template with the non-editable elements coming
// from the print product's reference card template (e.g. barCode based cards).
// Mirrors the logic used in useCardSettings#onCardSettingsUpdate when the print
// size changes.
const createBarCodeTemplateElements = async ({
  client,
  printProductCardId,
  printSizeId,
  orientation,
  cardTemplate,
  cardSide,
  cardElementCreate,
  cardElementConfigurationCreate,
  cardTemplateUpdate,
  designMode,
}) => {
  try {
    const {
      data: { card },
    } = await client.query({
      query: getPrintSizeCard,
      variables: {
        card_id: printProductCardId,
        print_size_id: printSizeId,
        orientation,
      },
    });

    const sizeCardSide = getCardSide({ card, side: CARD_SIDE.FRONT });
    const sizeNonEditableElementConfigs =
      sizeCardSide.card_element_configurations.filter(
        ({
          is_pro_editable: isEditable,
          photo_type: photoType,
          card_element: cardElement,
        }) =>
          !isEditable &&
          (photoType === PHOTO_TYPE.FOREGROUND ||
            !isFullFrame(cardElement.frame)),
      );

    // We will remove any elements that is not pro editable, as these are intrinsic
    // to the templates they come from.
    const keptTemplateElements = cardTemplate.card_elements.filter(element => {
      const cardElementConfiguration =
        cardSide.card_element_configurations.find(
          ({ card_element: elem }) => element.id === elem.id,
        );

      if (!cardElementConfiguration) {
        return true;
      }

      return cardElementConfiguration?.is_pro_editable;
    });

    const newNonEditableElements = await pMap(
      sizeNonEditableElementConfigs,
      async sizeElementConfig => {
        const { card_element: element } = sizeElementConfig;

        const {
          data: {
            cardElementCreate: { card_element: newElement },
          },
        } = await cardElementCreate({
          variables: {
            input: {
              type: element.type,
              frame: { ...element.frame, __typename: undefined },
              z_index: element.z_index,
            },
          },
        });

        const photoElementConfiguration = (() => {
          if (sizeElementConfig?.photo_element_configuration) {
            return sizeElementConfig?.photo_element_configuration;
          }

          if (sizeElementConfig?.asset_image) {
            return {
              photo_type: sizeElementConfig?.photo_type,
              existing_asset_image_id: sizeElementConfig?.asset_image?.id,
            };
          }

          if (designMode === DESIGN_MODES.ID_CARDS) {
            return {
              photo_type: sizeElementConfig?.photo_type,
            };
          }

          return undefined;
        })();

        const textElementConfiguration = (() => {
          if (sizeElementConfig?.text_element_configuration) {
            return sizeElementConfig?.text_element_configuration;
          }

          if (sizeElementConfig?.text_align) {
            return {
              text_align: sizeElementConfig.text_align,
            };
          }
        })();

        return {
          element: newElement,
          cardElementConfiguration: {
            is_editable: sizeElementConfig.is_editable,
            is_printable: sizeElementConfig.is_printable,
            is_pro_editable: sizeElementConfig.is_pro_editable,
            help_placeholder: sizeElementConfig.help_placeholder,
            photo_element_configuration: photoElementConfiguration,
            text_element_configuration: textElementConfiguration,
          },
        };
      },
      { concurrency: 5 },
    );

    const cardElementIds = [
      ...keptTemplateElements.map(({ id }) => id),
      ...newNonEditableElements.map(({ element }) => element.id),
    ];

    await cardTemplateUpdate({
      variables: {
        input: {
          card_template_id: cardTemplate.id,
          card_element_ids: cardElementIds,
        },
      },
    });

    //  Create configurations on the new card side matching those on the original
    await pMap(
      newNonEditableElements,
      async ({ element, cardElementConfiguration }) => {
        await cardElementConfigurationCreate({
          variables: {
            input: {
              card_side_id: cardSide.id,
              card_element_id: element.id,
              ...cardElementConfiguration,
            },
          },
        });
      },
      { concurrency: 5 },
    );
  } catch {
    //  Nothing to do here. worst case scenario card creation will follow up without these items.
  }
};

const useCardFetch = ({
  defaultCardId,
  defaultCardSideId,
  defaultCardOrientation,
  side,
  designMode,
  printPartnerId,
}) => {
  const [cardId, setCardId] = useState(defaultCardId);
  const [loadingCard, setLoadingCard] = useState(false);
  const openAlert = useOpenAlert();
  const client = useApolloClient();

  const [assetImagesUpload] = useMutation(assetImagesUploadMutation);
  const [cardCreate] = useMutation(cardCreateMutation);
  const [cardUpdate] = useMutation(cardUpdateMutation);
  const [cardElementCreate] = useMutation(cardElementCreateMutation);
  const [cardTemplateUpdate] = useMutation(cardTemplateUpdateMutation);
  const [cardElementConfigurationCreate] = useMutation(
    cardElementConfigurationCreateMutation,
  );

  const authenticated = useSelector(({ auth }) => auth.authenticated);

  const { data: { me } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });

  const { data: { printPartners } = {} } = useQuery(getPrintPartners, {
    variables: {
      // Fallback to bay
      name: printPartnerId ? undefined : 'Bay Photo',
      id: printPartnerId,
    },
  });

  const [printPartner] = printPartners?.records ?? [];

  const { data: { printProducts } = {} } = useQuery(getPrintProducts, {
    variables: { print_partner_id: printPartner?.id },
    skip: !printPartner,
  });
  const { data: { card } = {}, refetch } = useQuery(getCardQuery, {
    variables: {
      id: cardId,
    },
    skip: !cardId,
    notifyOnNetworkStatusChange: true,
  });

  const onCardCreate = async ({ printProduct, printSize }) => {
    if (loadingCard) {
      return;
    }

    setLoadingCard(true);

    try {
      // Create the initial card
      const cardResponse = await cardCreate({
        variables: {
          input: {
            card_template: {
              name: 'Card Template',
              side: CARD_SIDE.FRONT,
              print_product_id: printProduct.id,
              print_size_id: printSize.id,
              orientation: defaultCardOrientation,
            },
            card_side: {
              name: 'Card Side',
            },
            card_side_group: {
              name: 'Card Side Group',
            },
          },
        },
      });

      const createdCard = cardResponse.data.cardCreate.card;
      const [cardSide] =
        createdCard.card_side_groups.records[0].card_sides.records;
      const cardTemplate = cardSide.card_template;

      // For ID/barCode based cards, seed the template with the non-editable
      // elements coming from the print product's reference card template.
      // Since all the ID Card templates have a background element, don't need to create one.
      if (designMode === DESIGN_MODES.ID_CARDS && printProduct?.card?.id) {
        await createBarCodeTemplateElements({
          client,
          printProductCardId: printProduct.card.id,
          printSizeId: printSize.id,
          orientation: defaultCardOrientation,
          cardTemplate,
          cardSide,
          cardElementCreate,
          cardElementConfigurationCreate,
          cardTemplateUpdate,
          designMode,
        });
      } else {
        // Create the background element for this new template.
        const backgroundCardElementCreateResponse = await cardElementCreate({
          variables: {
            input: {
              type: CARD_ELEMENT_TYPE.PHOTO,
              z_index: 0,
              frame: {
                width: 1,
                height: 1,
                x: 0,
                y: 0,
              },
            },
          },
        });
        const backgroundCardElement =
          backgroundCardElementCreateResponse.data.cardElementCreate
            .card_element;

        // Add the background in the cardTemplate
        await cardTemplateUpdate({
          variables: {
            input: {
              card_template_id: cardTemplate.id,
              card_element_ids: [backgroundCardElement.id],
            },
          },
        });

        // Create the initial configuration of the CardElement
        await cardElementConfigurationCreate({
          variables: {
            input: {
              card_element_id: backgroundCardElement.id,
              card_side_id: cardSide.id,
              is_editable: false,
              photo_element_configuration: {
                background_color: [
                  DESIGN_MODES.COLLAGES,
                  DESIGN_MODES.SAVE_THE_DATE,
                ].includes(designMode)
                  ? '#9DBAF0'
                  : '#FFFFFF',
              },
            },
          },
        });

        // If for collages & user has a logo, create the logo element.
        if (designMode === DESIGN_MODES.COLLAGES) {
          // Create Powered By Waldo element
          await createEventCollageElements(
            cardSide,
            cardTemplate,
            backgroundCardElement,
            cardElementCreate,
            cardElementConfigurationCreate,
            cardTemplateUpdate,
            assetImagesUpload,
            printSize,
            me,
          );
        }
      }

      setCardId(createdCard.id);
    } catch (error) {
      openAlert({ type: 'error', message: getErrorString(error) });
    } finally {
      setLoadingCard(false);
    }
  };

  useEffect(() => {
    const defaultPrintProduct = getDefaultPrintProduct(printProducts?.records);
    let defaultPrintProductSize = (
      defaultPrintProduct?.sizes?.records ?? []
    ).find(
      printProductSize =>
        (printProductSize.print_size.name ?? '').replaceAll(' ', '') === '8x10',
    );

    const defaultBarCodePrintProductSize = (
      defaultPrintProduct?.sizes?.records ?? []
    ).find(printProductSize =>
      (printProductSize.print_size.name ?? '')
        .replaceAll(' ', '')
        .toLowerCase()
        .includes('idcard'),
    );

    // Focus on size as a secondary check
    if (!defaultPrintProductSize) {
      defaultPrintProductSize = (
        defaultPrintProduct?.sizes?.records ?? []
      ).find(
        printProductSize =>
          printProductSize.print_size.width === 8 &&
          printProductSize.print_size.height === 10,
      );
    }

    // If all else fails, rely on the 1st available.
    if (!defaultPrintProductSize) {
      defaultPrintProductSize = defaultPrintProduct?.sizes?.records?.[0];
    }

    // If there's no card - create it
    if (!cardId && defaultPrintProduct) {
      onCardCreate({
        printProduct: defaultPrintProduct,
        printSize:
          designMode === DESIGN_MODES.ID_CARDS &&
          defaultBarCodePrintProductSize?.print_size
            ? defaultBarCodePrintProductSize?.print_size
            : defaultPrintProductSize?.print_size,
      });
    }
  }, [printProducts, cardId]);

  return {
    loadingCard,
    card,
    cardSide: getCardSide({ card, side, cardSideId: defaultCardSideId }),
    refetchCard: refetch,
    cardUpdate,
  };
};

export default useCardFetch;
